import React, { createContext, useContext, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

/**
 * ReservationModal — pre-checkout form that captures the attendee's data in
 * ActiveCampaign (list 40 — "Lidera con Voz Propia") BEFORE redirecting to
 * the Stripe checkout page.
 *
 * Submission mirrors ActiveCampaign's own JSONP flow: we build the exact
 * query string their proc.php expects and load it as a <script>. AC's
 * proc.php responds with JS that calls window._show_thank_you or
 * window._show_error — we override those callbacks to redirect / toast.
 */

const AC_ENDPOINT = "https://estherparejo.activehosted.com/proc.php";
const AC_FORM_ID = "40";
const AC_ORIGIN = "30ea6d3d-38ba-4419-90ad-75b5e15ae381";
const PRIVACY_LABEL = "Acepto la política de privacidad.";

const ReservationContext = createContext({
  open: (_stripeUrl, _passLabel) => {},
});

export const useReservation = () => useContext(ReservationContext);

const initialForm = {
  firstname: "",
  lastname: "",
  email: "",
  phone: "",
  privacy: false,
};

const fieldClass =
  "w-full bg-transparent border-b border-[#3F403A]/25 focus:border-[#63846F] focus:outline-none py-3 px-1 font-sans text-base text-[#3F403A] placeholder:text-[#3F403A]/40 transition-colors";

const emailRe = /^[\+_a-z0-9\-'&=]+(\.[\+_a-z0-9\-']+)*@[a-z0-9\-]+(\.[a-z0-9\-]+)*(\.[a-z]{2,})$/i;

const submitToActiveCampaign = (form) =>
  new Promise((resolve, reject) => {
    // Override AC callbacks before loading the script.
    let settled = false;
    const cleanup = () => {
      settled = true;
      try {
        delete window._show_thank_you;
        delete window._show_error;
      } catch {
        window._show_thank_you = undefined;
        window._show_error = undefined;
      }
    };

    window._show_thank_you = (_id, _message, trackcmp_url) => {
      if (settled) return;
      // Fire AC tracking pixel (fire-and-forget).
      if (trackcmp_url) {
        try {
          const t = document.createElement("script");
          t.src = trackcmp_url;
          t.async = true;
          document.head.appendChild(t);
        } catch {
          /* ignore */
        }
      }
      cleanup();
      resolve();
    };

    window._show_error = (_id, message) => {
      if (settled) return;
      cleanup();
      reject(new Error(message || "No hemos podido enviar tu inscripción."));
    };

    // Build the exact query AC's own form uses.
    const params = new URLSearchParams();
    params.append("u", AC_FORM_ID);
    params.append("f", AC_FORM_ID);
    params.append("s", "");
    params.append("c", "0");
    params.append("m", "0");
    params.append("act", "sub");
    params.append("v", "2");
    params.append("or", AC_ORIGIN);
    params.append("firstname", form.firstname.trim());
    params.append("lastname", form.lastname.trim());
    params.append("email", form.email.trim());
    params.append("phone", form.phone.trim());
    // Privacy field expected as an array (checkbox group).
    params.append("field[9][]", "~|");
    params.append("field[9][]", PRIVACY_LABEL);
    params.append("jsonp", "true");

    const script = document.createElement("script");
    script.src = `${AC_ENDPOINT}?${params.toString()}`;
    script.async = true;
    script.onerror = () => {
      if (settled) return;
      cleanup();
      reject(new Error("No hemos podido contactar con el servidor de inscripción."));
    };
    document.head.appendChild(script);

    // Hard timeout — 12 s.
    setTimeout(() => {
      if (settled) return;
      cleanup();
      reject(new Error("Tiempo de espera agotado. Vuelve a intentarlo."));
    }, 12000);
  });

const ReservationModalInner = ({ opened, stripeUrl, passLabel, onOpenChange }) => {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);

  // Reset form each time the dialog opens.
  React.useEffect(() => {
    if (opened) setForm(initialForm);
  }, [opened]);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((s) => ({ ...s, [name]: type === "checkbox" ? checked : value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (
      !form.firstname.trim() ||
      !form.lastname.trim() ||
      !form.email.trim() ||
      !form.phone.trim()
    ) {
      toast.error("Por favor, completa todos los campos.");
      return;
    }
    if (!emailRe.test(form.email.trim())) {
      toast.error("Introduce una dirección de correo electrónico válida.");
      return;
    }
    if (!form.privacy) {
      toast.error("Debes aceptar la política de privacidad para continuar.");
      return;
    }
    setLoading(true);
    try {
      await submitToActiveCampaign(form);
      toast.success("Datos guardados. Te llevamos a la pasarela de pago…");
      // Small delay so the toast is visible before the redirect.
      setTimeout(() => {
        window.location.href = stripeUrl;
      }, 800);
    } catch (err) {
      setLoading(false);
      toast.error(err.message || "No hemos podido enviar tu inscripción.");
    }
  };

  return (
    <Dialog open={opened} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg bg-[#FAF8F3] border border-[#3F403A]/15 rounded-none p-0 gap-0"
        data-testid="reservation-modal"
      >
        <div className="px-6 md:px-10 pt-10 pb-8">
          <DialogHeader className="text-left space-y-3">
            <p className="overline text-[#63846F]">Inscripción · Paso 1 de 2</p>
            <DialogTitle className="font-serif text-3xl md:text-4xl leading-tight text-[#3F403A]">
              Reserva tu{" "}
              <span className="font-quiche text-[#63846F]">
                <strong className="quiche-bold font-extrabold">
                  {passLabel || "plaza"}
                </strong>
              </span>
            </DialogTitle>
            <DialogDescription className="font-sans text-sm md:text-base text-[#3F403A]/75 leading-relaxed">
              Déjanos tus datos antes de pasar a la pasarela de pago. Te
              enviaremos toda la información previa por email.
            </DialogDescription>
          </DialogHeader>

          <form
            onSubmit={onSubmit}
            className="mt-8 space-y-6"
            data-testid="reservation-form"
            noValidate
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <label className="block">
                <span className="overline text-[#3F403A]/60 block mb-2">
                  Nombre
                </span>
                <input
                  type="text"
                  name="firstname"
                  value={form.firstname}
                  onChange={onChange}
                  required
                  autoComplete="given-name"
                  placeholder="Tu nombre"
                  className={fieldClass}
                  data-testid="reservation-firstname"
                />
              </label>
              <label className="block">
                <span className="overline text-[#3F403A]/60 block mb-2">
                  Apellidos
                </span>
                <input
                  type="text"
                  name="lastname"
                  value={form.lastname}
                  onChange={onChange}
                  required
                  autoComplete="family-name"
                  placeholder="Tus apellidos"
                  className={fieldClass}
                  data-testid="reservation-lastname"
                />
              </label>
              <label className="block sm:col-span-2">
                <span className="overline text-[#3F403A]/60 block mb-2">
                  Correo electrónico
                </span>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={onChange}
                  required
                  autoComplete="email"
                  placeholder="nombre@empresa.com"
                  className={fieldClass}
                  data-testid="reservation-email"
                />
              </label>
              <label className="block sm:col-span-2">
                <span className="overline text-[#3F403A]/60 block mb-2">
                  Teléfono
                </span>
                <input
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={onChange}
                  required
                  autoComplete="tel"
                  placeholder="+34 600 000 000"
                  className={fieldClass}
                  data-testid="reservation-phone"
                />
              </label>
            </div>

            <label className="flex items-start gap-3 pt-2 cursor-pointer">
              <input
                type="checkbox"
                name="privacy"
                checked={form.privacy}
                onChange={onChange}
                required
                className="mt-1 h-4 w-4 accent-[#63846F] shrink-0"
                data-testid="reservation-privacy"
              />
              <span className="font-sans text-sm text-[#3F403A]/80 leading-relaxed">
                Acepto la política de privacidad y autorizo el tratamiento de
                mis datos para gestionar mi inscripción.
              </span>
            </label>

            <div className="pt-2">
              <button
                type="submit"
                disabled={loading}
                data-testid="reservation-submit"
                className="w-full inline-flex items-center justify-center gap-3 rounded-none border border-[#63846F] bg-[#63846F] text-[#FAF8F3] hover:bg-[#4E6D5A] hover:border-[#4E6D5A] transition-colors px-8 py-4 uppercase tracking-[0.22em] text-xs md:text-[13px] font-medium disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? "Enviando…" : "Continuar al pago"}
                {!loading && <span aria-hidden>→</span>}
              </button>
              <p className="mt-4 text-center font-sans text-xs text-[#3F403A]/60">
                Al continuar te llevaremos a la pasarela segura de Stripe.
              </p>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const ReservationProvider = ({ children }) => {
  const [opened, setOpened] = useState(false);
  const [stripeUrl, setStripeUrl] = useState("");
  const [passLabel, setPassLabel] = useState("");

  const open = useCallback((url, label) => {
    setStripeUrl(url || "");
    setPassLabel(label || "plaza");
    setOpened(true);
  }, []);

  const onOpenChange = (v) => setOpened(v);

  return (
    <ReservationContext.Provider value={{ open }}>
      {children}
      <ReservationModalInner
        opened={opened}
        stripeUrl={stripeUrl}
        passLabel={passLabel}
        onOpenChange={onOpenChange}
      />
    </ReservationContext.Provider>
  );
};

export default ReservationProvider;
